import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as Cesium from 'cesium';

function CompassWidget({ viewer, onHeadingChange, onPitchChange }) {
  const { t } = useTranslation();
  const [heading, setHeading] = useState(0);
  const [pitch, setPitch] = useState(-Math.PI / 4);
  const [isRotating, setIsRotating] = useState(false);
  const [isDraggingOuterRing, setIsDraggingOuterRing] = useState(false);
  const [hoveredPart, setHoveredPart] = useState(null);
  const [joystickOffset, setJoystickOffset] = useState({ x: 0, y: 0 });
  const [orbitCursorOpacity, setOrbitCursorOpacity] = useState(0);
  
  // Refs for compass functionality
  const compassRef = useRef(null);
  const outerRingRef = useRef(null);
  const innerControlRef = useRef(null);
  const isDragging = useRef(false);
  const joystickAnimationFrame = useRef(null);
  const currentVelocity = useRef({ heading: 0, pitch: 0 });
  const context = useRef({});
  const removePostRenderListener = useRef(null);
  const removeClockTickListener = useRef(null);
  const resetSpeed = Math.PI / 100;
  const rotateClick = useRef(false);
  const speedRef = useRef(0);

  // Cesium utility variables
  const vectorScratch = new Cesium.Cartesian2();
  const windowPositionScratch = new Cesium.Cartesian2();
  const clickLocationScratch = new Cesium.Cartesian2();
  const centerScratch = new Cesium.Cartesian3();
  const oldTransformScratch = new Cesium.Matrix4();
  const newTransformScratch = new Cesium.Matrix4();
  const pickRayScratch = new Cesium.Ray();

  const nominalTotalRadius = 47.5; // Half of 95px
  const nominalGyroRadius = 20; // Inner joystick area
  
  // Joystick response tuning
  const DEADZONE = 0.05;      // fraction of radius without movement
  const EXPONENT = 2;         // non-linear curve (3..4 for softer start)
  const MAX_RATE = 1 / 1000;  // keep existing top speed
  const SMOOTHING = 0.05;     // 0..1; higher = smoother/slower response

  // Setup smooth compass tracking using postRender for smooth updates
  useEffect(() => {
    if (!viewer || !viewer.scene) return;

    const updateHeading = () => {
      setHeading(viewer.camera.heading);
      setPitch(viewer.camera.pitch);
    };

    removePostRenderListener.current = viewer.scene.postRender.addEventListener(updateHeading);
    updateHeading();

    return () => {
      if (removePostRenderListener.current) {
        removePostRenderListener.current();
      }
      if (removeClockTickListener.current) {
        removeClockTickListener.current();
      }
    };
  }, [viewer]);

  // Utility functions from cesium-helpers
  const moveUpIfTooCloseToTerrain = () => {
    const controller = viewer.scene.screenSpaceCameraController;
    if (!controller.enableCollisionDetection) {
      return false;
    }
    const camera = viewer.camera;
    const height = viewer.scene.globe.getHeight(camera.positionCartographic);
    const distanceDiff = camera.positionCartographic.height - height - controller.minimumZoomDistance;
    if (Cesium.Math.lessThan(distanceDiff, 0.0, Cesium.Math.EPSILON1)) {
      viewer.camera.moveUp(-distanceDiff);
      return true;
    }
    return false;
  };

  const resetToNorth = () => {
    const camera = viewer.camera;
    const oldTransform = Cesium.Matrix4.clone(camera.transform, oldTransformScratch);
    camera.lookAtTransform(context.current.frame);
    const newCameraAngle = Cesium.Math.negativePiToPi(
      Cesium.Math.PI_OVER_TWO + Math.atan2(camera.position.y, camera.position.x)
    );
    const duration = Math.abs(newCameraAngle) / resetSpeed;

    let prevProgress = 0;
    const start = performance.now();
    const step = () => {
      const elapsed = performance.now() - start;
      const progress = Cesium.Math.clamp(elapsed / duration, 0, 1);

      camera.rotateLeft((progress - prevProgress) * newCameraAngle);

      prevProgress = progress;
      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        camera.lookAtTransform(oldTransform);
      }
    };
    window.requestAnimationFrame(step);
  };

  // Handle outer ring rotation (from cesium-helpers)
  const rotate = (cursorVector) => {
    const camera = viewer.camera;

    context.current.rotateInitialCursorAngle = Math.atan2(-cursorVector.y, cursorVector.x);

    const oldTransform = Cesium.Matrix4.clone(camera.transform, oldTransformScratch);

    camera.lookAtTransform(context.current.frame);
    context.current.rotateInitialCameraAngle = Math.atan2(camera.position.y, camera.position.x);
    camera.lookAtTransform(oldTransform);

    rotateClick.current = true;
    setIsDraggingOuterRing(true);

    document.addEventListener('pointermove', handleRotatePointerMove, false);
    document.addEventListener('pointerup', handleRotatePointerUp, false);
  };

  const handleRotatePointerMove = (event) => {
    if (moveUpIfTooCloseToTerrain()) {
      return;
    }
    const camera = viewer.camera;
    clickLocationScratch.x = event.clientX - context.current.compassRectangle.left;
    clickLocationScratch.y = event.clientY - context.current.compassRectangle.top;
    const vector = Cesium.Cartesian2.subtract(clickLocationScratch, context.current.compassCenter, vectorScratch);
    const angle = Math.atan2(-vector.y, vector.x);

    const angleDifference = angle - context.current.rotateInitialCursorAngle;
    const newCameraAngle = Cesium.Math.zeroToTwoPi(
      context.current.rotateInitialCameraAngle - angleDifference
    );

    const oldTransform = Cesium.Matrix4.clone(camera.transform, oldTransformScratch);
    camera.lookAtTransform(context.current.frame);
    const currentCameraAngle = Math.atan2(camera.position.y, camera.position.x);
    camera.rotateRight(newCameraAngle - currentCameraAngle);
    camera.lookAtTransform(oldTransform);

    rotateClick.current = false;
  };

  const handleRotatePointerUp = (event) => {
    document.removeEventListener('pointermove', handleRotatePointerMove, false);
    document.removeEventListener('pointerup', handleRotatePointerUp, false);
    
    setIsDraggingOuterRing(false);

    if (rotateClick.current) {
      resetToNorth();
    }
  };

  // Handle inner joystick orbit functionality
  const orbit = (cursorVector) => {
    context.current.orbitIsLook = !context.current.viewCenter;
    context.current.orbitLastTimestamp = performance.now();

    document.addEventListener('pointermove', handleOrbitPointerMove, false);
    document.addEventListener('pointerup', handleOrbitPointerUp, false);

    removeClockTickListener.current = viewer.clock.onTick.addEventListener(handleOrbitTick);

    updateAngleAndOpacity(cursorVector, context.current.compassRectangle.width);
  };

  const handleOrbitTick = () => {
    if (moveUpIfTooCloseToTerrain()) {
      return;
    }
    const camera = viewer.camera;
    const timestamp = performance.now();

    const deltaT = timestamp - context.current.orbitLastTimestamp;
    // Progressive speed mapping using non-linear curve and smoothing
    const rate = speedRef.current * MAX_RATE;
    const distance = deltaT * rate;

    // Invert both axes so drag direction matches camera motion
    const angle = context.current.orbitCursorAngle + Cesium.Math.PI_OVER_TWO + Math.PI;
    const x = Math.cos(angle) * distance;
    const y = Math.sin(angle) * distance;

    const oldTransform = Cesium.Matrix4.clone(camera.transform, oldTransformScratch);
    camera.lookAtTransform(context.current.frame);
    
    if (context.current.orbitIsLook) {
      camera.look(Cesium.Cartesian3.UNIT_Z, -x);
      camera.look(camera.right, -y);
    } else {
      camera.rotateLeft(x);
      
      // Limit pitch for map viewing: from horizon (0) to straight down (-π/2)
      const currentPitch = camera.pitch;
      const newPitchMovement = y;
      const proposedPitch = currentPitch + newPitchMovement;
      
      // Clamp pitch between 0 (horizon) and -π/2 (straight down)
      if (proposedPitch <= 0 && proposedPitch >= -Cesium.Math.PI_OVER_TWO) {
        camera.rotateUp(newPitchMovement);
      } else if (proposedPitch > 0) {
        // If trying to go above horizon, set to horizon
        camera.setView({
          orientation: {
            heading: camera.heading,
            pitch: 0,
            roll: camera.roll
          }
        });
      } else if (proposedPitch < -Cesium.Math.PI_OVER_TWO) {
        // If trying to go below straight down, set to straight down
        camera.setView({
          orientation: {
            heading: camera.heading,
            pitch: -Cesium.Math.PI_OVER_TWO,
            roll: camera.roll
          }
        });
      }
    }
    
    camera.lookAtTransform(oldTransform);
    context.current.orbitLastTimestamp = timestamp;
  };

  const updateAngleAndOpacity = (vector, compassWidth) => {
    const angle = Math.atan2(-vector.y, vector.x);
    context.current.orbitCursorAngle = Cesium.Math.zeroToTwoPi(angle - Cesium.Math.PI_OVER_TWO);

    const distance = Cesium.Cartesian2.magnitude(vector);
    const maxDistance = compassWidth / 2.0;
    const r = Math.min(distance / maxDistance, 1.0);

    // Deadzone and progressive curve for speed factor
    const rAdj = Math.max((r - DEADZONE) / (1 - DEADZONE), 0);
    const speedFactor = Math.pow(rAdj, EXPONENT); // 0..1

    // Smooth changes to avoid jumps
    speedRef.current = speedRef.current * (1 - SMOOTHING) + speedFactor * SMOOTHING;

    // Use speed to drive opacity (0.5..1.0)
    const newOpacity = 0.5 + 0.5 * speedRef.current;
    setOrbitCursorOpacity(newOpacity);

    // Update joystick visual position (keep subtle movement)
    const constrainedDistance = Math.min(distance, 4); // Max 4px movement
    const ratio = distance > 0 ? constrainedDistance / distance : 0;
    setJoystickOffset({ x: vector.x * ratio, y: vector.y * ratio });
  };

  const handleOrbitPointerMove = (event) => {
    clickLocationScratch.x = event.clientX - context.current.compassRectangle.left;
    clickLocationScratch.y = event.clientY - context.current.compassRectangle.top;
    const cursorVector = Cesium.Cartesian2.subtract(clickLocationScratch, context.current.compassCenter, vectorScratch);
    updateAngleAndOpacity(cursorVector, context.current.compassRectangle.width);
  };

  const handleOrbitPointerUp = () => {
    document.removeEventListener('pointermove', handleOrbitPointerMove, false);
    document.removeEventListener('pointerup', handleOrbitPointerUp, false);
    if (removeClockTickListener.current) {
      removeClockTickListener.current();
    }
    setOrbitCursorOpacity(0);
    setJoystickOffset({ x: 0, y: 0 });
    setIsRotating(false);
  };

  // Main pointer down handler (from cesium-helpers)
  const handlePointerDown = (event) => {
    const camera = viewer.camera;
    const compassElement = event.currentTarget;
    context.current.compassRectangle = compassElement.getBoundingClientRect();
    context.current.compassCenter = new Cesium.Cartesian2(
      (context.current.compassRectangle.right - context.current.compassRectangle.left) / 2,
      (context.current.compassRectangle.bottom - context.current.compassRectangle.top) / 2
    );
    clickLocationScratch.x = event.clientX - context.current.compassRectangle.left;
    clickLocationScratch.y = event.clientY - context.current.compassRectangle.top;
    const vector = Cesium.Cartesian2.subtract(clickLocationScratch, context.current.compassCenter, vectorScratch);
    const distanceFromCenter = Cesium.Cartesian2.magnitude(vector);

    windowPositionScratch.x = viewer.scene.canvas.clientWidth / 2;
    windowPositionScratch.y = viewer.scene.canvas.clientHeight / 2;
    const ray = camera.getPickRay(windowPositionScratch, pickRayScratch);
    context.current.viewCenter = viewer.scene.globe.pick(ray, viewer.scene, centerScratch);

    context.current.frame = Cesium.Transforms.eastNorthUpToFixedFrame(
      context.current.viewCenter ? context.current.viewCenter : camera.positionWC,
      Cesium.Ellipsoid.WGS84,
      newTransformScratch
    );

    const maxDistance = context.current.compassRectangle.width / 2;
    const distanceFraction = distanceFromCenter / maxDistance;

    if (distanceFraction < nominalGyroRadius / nominalTotalRadius) {
      setIsRotating(true);
      orbit(vector);
    } else if (distanceFraction < 1) {
      rotate(vector);
    }
    event.stopPropagation();
    event.preventDefault();
  };

  // Direct rotation transform like cesium-helpers (no degree conversion, use radians directly)
  const compassRotation = -heading;

  // Styles (keeping your original visual design)
  const styles = {
    container: {
      position: 'relative',
      width: '95px',
      height: '95px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      userSelect: 'none',
      backgroundColor: 'transparent',
    },
    outerRing: {
      position: 'absolute',
      width: '95px',
      height: '95px',
      borderRadius: '50%',
      cursor: isDraggingOuterRing ? 'grabbing' : (hoveredPart === 'outer' ? 'grab' : 'pointer'),
      transition: 'none', // No CSS transitions - let postRender handle smoothness
      transform: `rotate(${compassRotation}rad)`, // Use radians directly like cesium-helpers
      filter: hoveredPart === 'outer' ? 'brightness(1.2)' : 'brightness(1)',
    },
    innerControl: {
      position: 'absolute',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      cursor: isRotating ? 'grabbing' : 'grab',
      transition: isRotating ? 'none' : 'all 0.2s ease-in-out',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transform: `
        translate(${joystickOffset.x}px, ${joystickOffset.y}px) 
        scale(${isRotating ? 1.1 : (hoveredPart === 'inner' ? 1.05 : 1)})
      `,
      background: isRotating
        ? 'rgba(20,20,30,0.6)'
        : (hoveredPart === 'inner' ? 'rgba(20,20,30,0.8)' : 'rgba(20,20,30,0.6)'),
      filter: hoveredPart === 'inner' ? 'brightness(1.1)' : 'brightness(1)',
    }
  };

  // Your existing SVG components
  const OuterRingSVG = () => (
    <svg viewBox="0 0 95 95" style={{ width: '100%', height: '100%' }}>
      <defs>
        <mask id="ringMask">
          <rect x="0" y="0" width="95" height="95" fill="white"/>
          <circle cx="47.5" cy="47.5" r="28" fill="black"/>
        </mask>
        <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgba(255, 255, 255, 0.02)" />
          <stop offset="100%" stopColor="rgba(255, 255, 255, 0)" />
        </linearGradient>
      </defs>

      <circle cx="47.5" cy="47.5" r="44"
        fill="rgba(20,20,30,0.6)"
        mask="url(#ringMask)"/>
      
      {[...Array(72)].map((_, i) => {
        const degrees = i * 5;
        const angle = degrees * Math.PI / 180;
        const isNorth = degrees === 0;
        const isCardinal = degrees % 90 === 0;
        const isMajor = degrees % 45 === 0;
        
        if (!isCardinal && !isMajor) return null;
        
        const innerRadius = isNorth ? 40 : isCardinal ? 32 : 32;
        const outerRadius = 40;
        
        const x1 = 47.5 + innerRadius * Math.sin(angle);
        const y1 = 47.5 - innerRadius * Math.cos(angle);
        const x2 = 47.5 + outerRadius * Math.sin(angle);
        const y2 = 47.5 - outerRadius * Math.cos(angle);
        
        return (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={isNorth ? "rgba(255, 255, 255, 0.8)" : isCardinal ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.5)"}
            strokeWidth={isCardinal ? "2.5" : "1."}
          />
        );
      })}
      
      <text x="47.5" y="16" 
        textAnchor="middle" 
        fill="rgba(255, 255, 255, 0.95)" 
        fontSize="12" 
        fontWeight="800" 
        fontFamily="Inter, system-ui, -apple-system, sans-serif"
        letterSpacing="0.5">N</text>
    </svg>
  );

  const InnerControlSVG = () => (
    <svg viewBox="0 0 40 40" style={{ width: '100%', height: '100%' }}>
      <defs>
        <linearGradient id="lightingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgba(255, 255, 255, 0.25)" />
          <stop offset="100%" stopColor="rgba(255, 255, 255, 0)" />
        </linearGradient>
      </defs>
      
      <ellipse cx="20" cy="20" rx="4" ry="12" 
        fill="none" 
        stroke="rgba(255, 255, 255, 0.9)" 
        strokeWidth="1.8"/>
      
      <ellipse cx="20" cy="20" rx="12" ry="4" 
        fill="none" 
        stroke="rgba(255, 255, 255, 0.9)" 
        strokeWidth="1.8"/>
    </svg>
  );

  return (
    <div 
      ref={compassRef}
      style={styles.container}
      onPointerDown={handlePointerDown}
    >
      {/* Outer Ring */}
      <div
        ref={outerRingRef}
        style={styles.outerRing}
        onMouseEnter={() => setHoveredPart('outer')}
        onMouseLeave={() => setHoveredPart(null)}
      >
        <OuterRingSVG />
      </div>
      
      {/* Inner Control */}
      <div
        ref={innerControlRef}
        style={styles.innerControl}
        onMouseEnter={() => setHoveredPart('inner')}
        onMouseLeave={() => setHoveredPart(null)}
        title={t('CompassJoystickTooltip')}
      >
        <InnerControlSVG />
      </div>
    </div>
  );
}

export default CompassWidget;
